import { Fragment, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Modal } from '@/components/ui/daisy';
import { useModalDialog } from '@/components/ui/daisy/modal/hooks';
import QuestionFormsOrchestratorProvider, {
  SubmitAllResult,
  useQuestionFormsOrchestrator,
} from '@/data/context/QuestionFormsOrchestrator';
import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import { BreadcrumbItem } from '@/data/hook/useDynamicBreadcrumbItems';
import useModalView from '@/data/hook/question/useModalView';
import useQuestionSession from '@/data/hook/question/useQuestionSession';
import useQuestions from '@/data/hook/question/useQuestions';
import Activity from '@/models/Activity';
import { Question, QuestionTypeEnum } from '@/models/Question';
import alert from '@/utils/shared/alert';
import CreateQuestionHeader from './CreateQuestionHeader';
import EditorView from './EditorView';

type CreateQuestionModalProps = {
  activity: Activity;
  cacheManager: ListCacheManager<Activity>;
  visible: boolean;
  initialQuestion?: Question;
  onClose: () => void;
  breadcrumbItems?: BreadcrumbItem[];
  allowedTypes?: QuestionTypeEnum[];
};

export default function CreateQuestionModal({
  activity,
  cacheManager,
  visible,
  initialQuestion,
  onClose,
  breadcrumbItems,
  allowedTypes,
}: CreateQuestionModalProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal',
  });

  const { questions } = useQuestions({ activity, cacheManager });

  const handleFormSaved = useCallback(
    ({ successes, failedIdentifiers }: SubmitAllResult) => {
      const questionsMap = new Map(
        questions.map((q: Question) => [q.identifier, q]),
      );
      for (const { result } of successes) {
        questionsMap.set(result.identifier, result);
      }

      const updatedQuestions = [...questionsMap.values()].sort(
        (a, b) => a.order - b.order,
      );

      cacheManager.update({
        ...activity,
        questions: updatedQuestions,
      });

      if (!failedIdentifiers.length) alert.success(t('saveSuccess'));
    },
    [cacheManager, activity, questions, t],
  );

  return (
    <QuestionFormsOrchestratorProvider onSaved={handleFormSaved}>
      <CreateQuestionModalInner
        breadcrumbItems={breadcrumbItems}
        activity={activity}
        cacheManager={cacheManager}
        visible={visible}
        initialQuestion={initialQuestion}
        onClose={onClose}
        allowedTypes={allowedTypes}
      />
    </QuestionFormsOrchestratorProvider>
  );
}

function CreateQuestionModalInner({
  activity,
  cacheManager,
  visible,
  onClose,
  initialQuestion,
  breadcrumbItems,
  allowedTypes,
}: CreateQuestionModalProps) {
  const { ref: dialogRef, close } = useModalDialog(visible, onClose);

  return (
    <Modal ref={dialogRef} className="modal-middle">
      <Modal.Box className="max-xl:min-w-11/12 min-w-4/5 aspect-video flex flex-col gap-8">
        <ConditionalRenderer condition={visible}>
          <CreateQuestionModalContent
            onClose={close}
            activity={activity}
            cacheManager={cacheManager}
            initialQuestion={initialQuestion}
            breadcrumbItems={breadcrumbItems}
            allowedTypes={allowedTypes}
          />
        </ConditionalRenderer>
        <Modal.Close />
      </Modal.Box>
      <Modal.Backdrop />
    </Modal>
  );
}

type CreateQuestionModalContentProps = Omit<
  CreateQuestionModalProps,
  'visible'
>;

const CreateQuestionModalContent = ({
  activity,
  cacheManager,
  initialQuestion,
  onClose,
  breadcrumbItems,
  allowedTypes,
}: CreateQuestionModalContentProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal',
  });

  const { questions: initialQuestions, deleteQuestionAsync: deleteQuestion } =
    useQuestions({
      activity,
      cacheManager,
    });

  const { isSaving, submitAll, hasUnsavedChanges } =
    useQuestionFormsOrchestrator();

  const {
    questions,
    activeIndex,
    addQuestion,
    replaceQuestion,
    removeQuestion,
    setActive,
  } = useQuestionSession({
    initialQuestions,
    isSaving,
    initialQuestion,
    activityId: activity.id,
  });

  const {
    view,
    selectedType,
    selectTypology,
    reset: resetView,
  } = useModalView(questions[activeIndex]);

  const resetToCreateView = () => {
    resetView();
    setActive(-1);
  };

  const handleSelectTypology = async (type: QuestionTypeEnum) => {
    selectTypology(type);
    const activeQuestion = questions[activeIndex];
    if (!activeQuestion) {
      addQuestion(type);
      return;
    }

    if (!activeQuestion.id) {
      replaceQuestion(activeIndex, type);
      return;
    }

    await deleteQuestion({
      identifier: activeQuestion.identifier,
      shouldShowAlert: false,
    }).catch(() => {});
    replaceQuestion(activeIndex, type);
  };

  const autoSelectedRef = useRef(false);

  useEffect(() => {
    if (view === 'editor') {
      autoSelectedRef.current = false;
      return;
    }

    const singleType = allowedTypes?.length === 1 ? allowedTypes[0] : null;

    if (singleType && !questions[activeIndex] && !autoSelectedRef.current) {
      autoSelectedRef.current = true;
      selectTypology(singleType);
      addQuestion(singleType);
    }
  }, [view, allowedTypes, questions, activeIndex, selectTypology, addQuestion]);

  const handleSubmitAll = async () => {
    const { failedIdentifiers } = await submitAll();
    if (!failedIdentifiers.length) return;

    const firstFailedIndex = questions.findIndex(question =>
      failedIdentifiers.includes(question.identifier),
    );
    if (firstFailedIndex < 0) return;

    alert.action(t('incompleteBlockTitle'), {
      description: t('incompleteBlockDescription'),
      action: {
        label: t('incompleteBlockFix'),
        onClick: () => handleSetActive(firstFailedIndex),
      },
    });
  };

  const handleSetActive = (index: number) => {
    selectTypology(questions[index].type);
    setActive(index);
  };

  const handleDeleteQuestion = async (index: number) => {
    const question = questions[index];
    removeQuestion(index);
    if (questions.length === 1) resetToCreateView();
    if (question.id) {
      await deleteQuestion({
        identifier: question.identifier,
        shouldShowAlert: true,
      }).catch(() => {});
    }
  };

  return (
    <Fragment>
      <CreateQuestionHeader
        view={view}
        selectedType={selectedType}
        breadcrumbItems={breadcrumbItems}
        activity={activity}
        onSelectTypology={handleSelectTypology}
        isSaving={isSaving}
        hasUnsavedChanges={hasUnsavedChanges}
        onSave={handleSubmitAll}
        onClose={onClose}
        allowedTypes={allowedTypes}
      />

      <ConditionalRenderer condition={questions.length > 0}>
        <EditorView
          questions={questions}
          activeIndex={activeIndex}
          view={view}
          activity={activity}
          onSelectActive={handleSetActive}
          onAddQuestion={resetToCreateView}
          onDeleteQuestion={handleDeleteQuestion}
        />
      </ConditionalRenderer>
    </Fragment>
  );
};
